import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import {
  useImportSniperMarkets,
  useCreateSniperMarkets,
} from '../../api/useSniper';
import type {
  SniperCounter,
  SniperMarketDraft,
  SniperPeriod,
  SniperTeam,
} from '../../types/sniper.types';
import { SNIPER_COUNTERS } from '../../types/sniper.types';
import {
  candidateToDraft,
  counterLabel,
  DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
  defaultLadderBands,
  draftIncompleteReason,
  gatePct,
  isDraftComplete,
  ladderSummary,
  normalizeDecimalInput,
  SNIPER_FORCED_LINE,
  venueColor,
  venueLabel,
} from '../../utils/sniperArm';
import type { BulkLadderOverlay } from '../../utils/sniperBulk';
import { SniperConfigPopover } from './SniperConfigPopover';
import { SniperTriState } from './SniperTriState';
import { NO_SIZE_HELP, NO_SIZE_LABEL } from '../shared/sniperNoSize';

/**
 * Bulk defaults stamped onto every draft by "Apply to all". A blank number and a
 * `''` switch both mean "leave that column untouched" — the same convention the
 * per-event `SniperBulkBar` uses.
 */
interface BulkDefaults {
  size: number | null;
  confidencePct: number | null;
  noMaxPrice: number | null;
  noSize: number | null;
  autoNoEnabled: string;
  autoNoIncludeExtraTime: string;
  sellOnVar: string;
  sellOnAnnulledGoal: string;
  varRebuyEnabled: string;
  /** `null` = leave each draft's ladder as it is. */
  ladder: BulkLadderOverlay | null;
}

const EMPTY_BULK: BulkDefaults = {
  size: null,
  // Blank like every sibling column — "Apply to all" must never stamp a gate the
  // operator did not type. Each row already carries the create default from
  // `candidateToDraft`; pre-filling this one would silently reset every row's.
  confidencePct: null,
  noMaxPrice: null,
  noSize: null,
  autoNoEnabled: '',
  autoNoIncludeExtraTime: '',
  sellOnVar: '',
  sellOnAnnulledGoal: '',
  varRebuyEnabled: '',
  ladder: null,
};

/** `''` = untouched; otherwise the boolean the tri-state select carries. */
function triStateValue(raw: string): boolean | null {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return null;
}

/**
 * Controlled decimal input that PRESERVES in-progress text (so `"0."` survives
 * long enough to reach `"0.5"`) and accepts a comma decimal separator. The prior
 * `value={String(draft.x)}` + `Number(...)` round-trip dropped the trailing dot
 * (`Number("0.") === 0` → re-rendered to `"0"`), making decimals untypable.
 *
 * Holds a LOCAL `text` string and emits a parsed number (or `null` for an empty
 * / bare-dot field). A `useEffect` syncs from the incoming `value` ONLY when it
 * diverges from what `text` currently parses to — so an external update (the
 * bulk "Apply to all", a reset, the initial guess) lands without clobbering what
 * the operator is mid-typing.
 */
function DecimalField({
  value,
  onValueChange,
  label,
  width,
  ariaLabel,
  disabled,
  helperText,
  placeholder,
}: {
  value: number | null;
  onValueChange: (n: number | null) => void;
  label: string;
  width: number;
  ariaLabel: string;
  disabled?: boolean;
  helperText?: string;
  placeholder?: string;
}) {
  const [text, setText] = useState<string>(value == null ? '' : String(value));

  useEffect(() => {
    const current = text === '' || text === '.' ? null : Number(text);
    if (value !== current) {
      setText(value == null ? '' : String(value));
    }
    // Intentionally keyed on the incoming numeric `value` only; reading `text`
    // here would re-sync on every keystroke and clobber in-progress typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <TextField
      size="small"
      label={label}
      value={text}
      disabled={disabled}
      helperText={helperText}
      placeholder={placeholder}
      onChange={(e) => {
        const normalized = normalizeDecimalInput(e.target.value);
        setText(normalized);
        onValueChange(
          normalized === '' || normalized === '.' ? null : Number(normalized),
        );
      }}
      inputProps={{ inputMode: 'decimal', 'aria-label': ariaLabel }}
      sx={{ width }}
    />
  );
}

/**
 * The confidence gate, edited as an INTEGER PERCENT over the 0..1 wire value —
 * the same mapping `SniperMarketRow` uses. It is deliberately NOT a
 * `DecimalField`: `pctToGate` (the row's editor) rejects anything below 1%, so a
 * market imported with a fractional gate would render as `0` there and
 * permanently disable its ✓. `null` = empty, which the bulk bar reads as
 * "leave untouched" and a row reports as an incomplete gate.
 */
function PercentField({
  value,
  onValueChange,
  label,
  width,
  ariaLabel,
}: {
  value: number | null;
  onValueChange: (n: number | null) => void;
  label: string;
  width: number;
  ariaLabel: string;
}) {
  const [text, setText] = useState<string>(value == null ? '' : String(value));

  useEffect(() => {
    const current = text === '' ? null : Number(text);
    if (value !== current) setText(value == null ? '' : String(value));
    // Keyed on the incoming value only — see `DecimalField`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <TextField
      size="small"
      label={label}
      value={text}
      onChange={(e) => {
        const next = e.target.value.replace(/[^0-9]/g, '');
        setText(next);
        onValueChange(next === '' ? null : Number(next));
      }}
      inputProps={{ inputMode: 'numeric', maxLength: 3, 'aria-label': ariaLabel }}
      sx={{ width }}
    />
  );
}

/**
 * Compact labelled toggle for one of a draft's booleans. The MUI `Switch` is a
 * real DOM element so the `Tooltip` anchors directly to the `FormControlLabel`
 * (no wrapping span needed, unlike the plain-function `DecimalField`).
 */
function FlagSwitch({
  checked,
  onCheckedChange,
  label,
  tooltip,
  ariaLabel,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  label: string;
  tooltip: string;
  ariaLabel: string;
  disabled?: boolean;
}) {
  return (
    <Tooltip title={tooltip}>
      <FormControlLabel
        sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 11, color: 'common.white' } }}
        control={
          <Switch
            size="small"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onCheckedChange(e.target.checked)}
            inputProps={{ 'aria-label': ariaLabel }}
          />
        }
        label={label}
      />
    </Tooltip>
  );
}

/** One editable candidate row inside the review step. Exported for testing. */
export function CandidateRow({
  draft,
  homeTeam,
  awayTeam,
  onChange,
}: {
  draft: SniperMarketDraft;
  homeTeam: string;
  awayTeam: string;
  onChange: (next: SniperMarketDraft) => void;
}) {
  const teamRequired =
    draft.counter === 'team_goals' ||
    draft.counter === 'team_corners' ||
    draft.counter === 'first_team_to_score';
  const isCorners =
    draft.counter === 'match_corners' || draft.counter === 'team_corners';
  // BTTS/FTTS have a FORCED line the operator does NOT own.
  const lineFixed =
    draft.counter === 'both_teams_to_score' ||
    draft.counter === 'first_team_to_score';
  const reason = draftIncompleteReason(draft);
  const complete = reason === null;
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{
        py: 0.5,
        px: 0.75,
        borderRadius: 1,
        border: 1,
        borderColor: complete ? 'divider' : 'warning.main',
        flexWrap: 'nowrap',
        overflowX: 'auto',
      }}
    >
      <Chip
        size="small"
        label={venueLabel(draft.venue)}
        sx={{ bgcolor: venueColor(draft.venue), color: '#fff', fontWeight: 700 }}
      />
      <Typography variant="body2" sx={{ minWidth: 120, flex: 1, fontSize: 12 }}>
        {draft.marketLabel}
      </Typography>

      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel id={`counter-${draft.venue}-${draft.marketId}`}>
          Counter
        </InputLabel>
        <Select
          labelId={`counter-${draft.venue}-${draft.marketId}`}
          label="Counter"
          displayEmpty
          value={draft.counter ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            const counter = (raw === '' ? null : raw) as SniperCounter | null;
            const nowTeam =
              counter === 'team_goals' ||
              counter === 'team_corners' ||
              counter === 'first_team_to_score';
            const nowCorners =
              counter === 'match_corners' || counter === 'team_corners';
            // BTTS/FTTS: force the contract line (1.5/0.5); otherwise keep the
            // operator's line. BTTS is match-wide (team null), FTTS team-scoped.
            const forcedLine = counter ? SNIPER_FORCED_LINE[counter] : undefined;
            onChange({
              ...draft,
              counter,
              // Set a team iff team_* / FTTS; clear it for match-wide / unset.
              team: nowTeam ? (draft.team ?? 'home') : null,
              // Corners are full-match only — force the half back to 'full' so
              // the row can't be left in a server-rejected state. Goal-family
              // (incl. BTTS/FTTS) KEEP the operator's chosen period.
              period: nowCorners ? 'full' : draft.period,
              line: forcedLine != null ? forcedLine : draft.line,
            });
          }}
        >
          <MenuItem value="">
            <em>— select —</em>
          </MenuItem>
          {SNIPER_COUNTERS.map((c) => (
            <MenuItem key={c} value={c}>
              {counterLabel(c)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 95 }} disabled={!teamRequired}>
        <InputLabel id={`team-${draft.venue}-${draft.marketId}`}>Team</InputLabel>
        <Select
          labelId={`team-${draft.venue}-${draft.marketId}`}
          label="Team"
          value={teamRequired ? (draft.team ?? '') : ''}
          onChange={(e) =>
            onChange({ ...draft, team: (e.target.value || null) as SniperTeam | null })
          }
        >
          <MenuItem value="home">{homeTeam || 'Home'}</MenuItem>
          <MenuItem value="away">{awayTeam || 'Away'}</MenuItem>
        </Select>
        {!teamRequired && (
          <FormHelperText>counter is match-wide</FormHelperText>
        )}
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 105 }} disabled={isCorners}>
        <InputLabel id={`period-${draft.venue}-${draft.marketId}`}>
          Period
        </InputLabel>
        <Select
          labelId={`period-${draft.venue}-${draft.marketId}`}
          label="Period"
          value={draft.period}
          onChange={(e) =>
            onChange({ ...draft, period: e.target.value as SniperPeriod })
          }
        >
          <MenuItem value="full">Full</MenuItem>
          <MenuItem value="first_half">1st Half</MenuItem>
          <MenuItem value="second_half">2nd Half</MenuItem>
        </Select>
        {isCorners && <FormHelperText>corners are full-match</FormHelperText>}
      </FormControl>

      <DecimalField
        label="Line"
        ariaLabel="line"
        width={62}
        value={draft.line}
        onValueChange={(n) => onChange({ ...draft, line: n })}
        disabled={lineFixed}
        helperText={
          lineFixed
            ? `fixed ${SNIPER_FORCED_LINE[draft.counter as SniperCounter]}`
            : undefined
        }
      />
      <DecimalField
        label="Size"
        ariaLabel="size"
        width={62}
        value={draft.size}
        onValueChange={(n) => onChange({ ...draft, size: n ?? 0 })}
      />
      <Tooltip title="Confidence gate — the LSports confidence the ladder starts buying at">
        <span>
          <PercentField
            label="Gate %"
            ariaLabel="confidence gate percent"
            width={70}
            value={gatePct(draft.confidenceGate)}
            onValueChange={(n) =>
              onChange({
                ...draft,
                confidenceGate: n == null ? 0 : Math.max(0, Math.min(1, n / 100)),
              })
            }
          />
        </span>
      </Tooltip>
      {/* The ladder is a TABLE — it cannot render at this row density, so the
          row opens the SAME config popover the market row uses. */}
      <SniperConfigPopover
        value={{
          ladderBands: draft.ladderBands,
          floorPrice: draft.floorPrice,
          varPreventiveExitOffset: draft.varPreventiveExitOffset,
        }}
        floorRequired={draft.sellOnVar || draft.sellOnAnnulledGoal}
        // CREATE can express an absent floor (the payload just omits it), so a
        // row that becomes buy-and-hold may drop the floor it was given.
        floorClearable
        onApply={(next) =>
          onChange({
            ...draft,
            ladderBands: next.ladderBands,
            floorPrice: next.floorPrice,
            // A cleared cell means the engine default, which is this number.
            varPreventiveExitOffset:
              next.varPreventiveExitOffset ?? DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
          })
        }
      />

      {/* Auto-NO period-close tail — off by default. The No $ ceiling and the
          leg's own size only matter when it is enabled. */}
      <Tooltip title="Auto-NO — sweep the No/Under side at period close">
        <FormControlLabel
          sx={{ ml: 0 }}
          control={
            <Checkbox
              size="small"
              checked={draft.autoNoEnabled}
              onChange={(e) =>
                onChange({ ...draft, autoNoEnabled: e.target.checked })
              }
              inputProps={{ 'aria-label': 'auto-NO enabled' }}
            />
          }
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
              auto-NO
            </Typography>
          }
        />
      </Tooltip>
      <DecimalField
        label="No $"
        ariaLabel="no max price"
        width={62}
        value={draft.noMaxPrice}
        onValueChange={(n) => onChange({ ...draft, noMaxPrice: n })}
      />
      <Tooltip title={NO_SIZE_HELP}>
        <span>
          <DecimalField
            label={NO_SIZE_LABEL}
            ariaLabel="no size"
            width={70}
            placeholder="= Size"
            value={draft.noSize}
            onValueChange={(n) => onChange({ ...draft, noSize: n })}
            disabled={!draft.autoNoEnabled}
          />
        </span>
      </Tooltip>
      <Tooltip title="Include extra time in the auto-NO tail (only meaningful for full / 2nd-half markets)">
        <FormControlLabel
          sx={{ ml: 0 }}
          control={
            <Switch
              size="small"
              checked={draft.autoNoIncludeExtraTime}
              disabled={!draft.autoNoEnabled || draft.period === 'first_half'}
              onChange={(e) =>
                onChange({
                  ...draft,
                  autoNoIncludeExtraTime: e.target.checked,
                })
              }
              inputProps={{ 'aria-label': 'auto-NO include extra time' }}
            />
          }
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
              ET
            </Typography>
          }
        />
      </Tooltip>

      <FlagSwitch
        label="Sell on VAR"
        ariaLabel="sell on var"
        tooltip="Post the exit the moment a VAR review follows our goal. Off = ride the review out."
        checked={draft.sellOnVar}
        onCheckedChange={(v) => onChange({ ...draft, sellOnVar: v })}
      />
      <FlagSwitch
        label="Sell on annulled goal"
        ariaLabel="sell on annulled goal"
        tooltip="Dump to the floor once the goal is annulled (or the score reverts below the line). Off = the operator unwinds by hand."
        checked={draft.sellOnAnnulledGoal}
        onCheckedChange={(v) => onChange({ ...draft, sellOnAnnulledGoal: v })}
      />
      <FlagSwitch
        label="Rebuy"
        ariaLabel="var rebuy enabled"
        tooltip="All-clear rebuy — restore the position if the VAR ultimately CONFIRMS the goal (the annulment was cleared)."
        checked={draft.varRebuyEnabled}
        onCheckedChange={(v) => onChange({ ...draft, varRebuyEnabled: v })}
      />
      {!complete && (
        <Tooltip title={`incomplete — ${reason}`}>
          <Chip
            size="small"
            color="warning"
            label={`incomplete · ${reason}`}
            sx={{ height: 20 }}
          />
        </Tooltip>
      )}
    </Stack>
  );
}

/**
 * Import-by-link flow: paste a Kalshi EVENT url → scrape candidate markets
 * (backend guesses counter/line) → review + fix each draft → confirm-create.
 * Every configurable field is reachable here, per row: what the row shows is
 * exactly what gets persisted. NEVER auto-persists a guess: only COMPLETE drafts
 * (`isDraftComplete`) are posted; `enabled` defaults OFF.
 */
export function SniperImportDialog({
  open,
  onClose,
  fixtureId,
  homeTeam,
  awayTeam,
}: {
  open: boolean;
  onClose: () => void;
  fixtureId: number;
  homeTeam: string;
  awayTeam: string;
}) {
  const [kalshiUrl, setKalshiUrl] = useState('');
  const [drafts, setDrafts] = useState<SniperMarketDraft[] | null>(null);
  const [bulk, setBulk] = useState<BulkDefaults>(EMPTY_BULK);

  const importMut = useImportSniperMarkets();
  const createMut = useCreateSniperMarkets();

  /**
   * Stamp the bulk defaults onto EVERY draft. A blank number / `''` switch
   * leaves that column untouched, and each row's counter/line/team stay as-is,
   * so the valid totals become complete while unclassified rows still need an
   * explicit counter.
   */
  const applyToAll = () => {
    const sellOnVar = triStateValue(bulk.sellOnVar);
    const sellOnAnnulledGoal = triStateValue(bulk.sellOnAnnulledGoal);
    const varRebuyEnabled = triStateValue(bulk.varRebuyEnabled);
    const autoNoEnabled = triStateValue(bulk.autoNoEnabled);
    const autoNoIncludeExtraTime = triStateValue(bulk.autoNoIncludeExtraTime);
    setDrafts((prev) =>
      prev
        ? prev.map((d) => ({
            ...d,
            size: bulk.size != null ? bulk.size : d.size,
            confidenceGate:
              bulk.confidencePct != null
                ? Math.max(0, Math.min(1, bulk.confidencePct / 100))
                : d.confidenceGate,
            noMaxPrice: bulk.noMaxPrice != null ? bulk.noMaxPrice : d.noMaxPrice,
            noSize: bulk.noSize != null ? bulk.noSize : d.noSize,
            sellOnVar: sellOnVar ?? d.sellOnVar,
            sellOnAnnulledGoal: sellOnAnnulledGoal ?? d.sellOnAnnulledGoal,
            varRebuyEnabled: varRebuyEnabled ?? d.varRebuyEnabled,
            autoNoEnabled: autoNoEnabled ?? d.autoNoEnabled,
            // Mirrors the row's own gate: a 1st-half market has no extra time,
            // which is exactly why its ET switch is disabled — a bulk stamp must
            // not set behind a control the operator cannot reach.
            autoNoIncludeExtraTime:
              d.period === 'first_half'
                ? false
                : (autoNoIncludeExtraTime ?? d.autoNoIncludeExtraTime),
            // Only what the operator actually edited inside the popover — a row
            // whose own rungs were tuned keeps them when the bulk only set a floor.
            ...(bulk.ladder?.touched.ladderBands
              ? { ladderBands: bulk.ladder.ladderBands }
              : {}),
            ...(bulk.ladder?.touched.floorPrice
              ? { floorPrice: bulk.ladder.floorPrice }
              : {}),
            ...(bulk.ladder?.touched.varPreventiveExitOffset
              ? { varPreventiveExitOffset: bulk.ladder.varPreventiveExitOffset }
              : {}),
          }))
        : prev,
    );
  };

  const runImport = () => {
    const url = kalshiUrl.trim();
    if (!url) {
      toast.error('Paste the Kalshi event URL.');
      return;
    }
    importMut.mutate(
      // Team names let the guesser auto-fill the team on team-goal markets.
      { fixtureId, dto: { kalshiEventUrl: url, homeTeam, awayTeam } },
      {
        onSuccess: (candidates) => {
          setDrafts(candidates.map(candidateToDraft));
          if (candidates.length === 0) {
            toast.info('No candidate markets found for that URL.');
          }
        },
        onError: () => {
          toast.error('Import failed — check the URL.');
        },
      },
    );
  };

  const completeDrafts = useMemo(
    () => (drafts ?? []).filter(isDraftComplete),
    [drafts],
  );

  const confirm = () => {
    if (completeDrafts.length === 0) return;
    createMut.mutate(
      { fixtureId, markets: completeDrafts },
      {
        onSuccess: () => {
          toast.success(`Added ${completeDrafts.length} market(s).`);
          handleClose();
        },
        onError: () => {
          toast.error('Failed to save markets.');
        },
      },
    );
  };

  const handleClose = () => {
    setDrafts(null);
    setKalshiUrl('');
    setBulk(EMPTY_BULK);
    onClose();
  };

  const bulkSelectSx = { width: 104 };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth sx={{ '& .MuiDialog-paper': { maxWidth: '1600px', width: '96vw' } }}>
      <DialogTitle>Import markets by event link</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField
              size="small"
              label="Kalshi event URL"
              value={kalshiUrl}
              onChange={(e) => setKalshiUrl(e.target.value)}
              fullWidth
            />
            <Button
              variant="outlined"
              onClick={runImport}
              disabled={importMut.isLoading}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {importMut.isLoading ? 'Scraping…' : 'Scrape'}
            </Button>
          </Stack>

          {importMut.isLoading && (
            <Stack alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={22} />
            </Stack>
          )}

          {drafts != null && (
            <>
              <Divider />
              <Alert severity="info" sx={{ py: 0 }}>
                Review the auto-guessed counter / line. Only complete rows are
                saved; markets are created DISABLED (fail-safe OFF).
              </Alert>
              {drafts.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5 }}>
                    Bulk defaults:
                  </Typography>
                  <DecimalField
                    label="Size"
                    ariaLabel="bulk size"
                    width={90}
                    value={bulk.size}
                    onValueChange={(n) => setBulk((b) => ({ ...b, size: n }))}
                  />
                  <PercentField
                    label="Gate %"
                    ariaLabel="bulk confidence percent"
                    width={90}
                    value={bulk.confidencePct}
                    onValueChange={(n) =>
                      setBulk((b) => ({ ...b, confidencePct: n }))
                    }
                  />
                  <DecimalField
                    label="No $"
                    ariaLabel="bulk no max price"
                    width={90}
                    value={bulk.noMaxPrice}
                    onValueChange={(n) => setBulk((b) => ({ ...b, noMaxPrice: n }))}
                  />
                  <DecimalField
                    label="No Size"
                    ariaLabel="bulk no size"
                    width={90}
                    value={bulk.noSize}
                    onValueChange={(n) => setBulk((b) => ({ ...b, noSize: n }))}
                  />
                  <SniperTriState
                    label="Auto-NO"
                    tooltip="Auto-NO period-close tail — buy the NO the instant the period ends without the Over crossing"
                    ariaLabel="bulk auto-no enabled"
                    value={bulk.autoNoEnabled}
                    onChange={(v) => setBulk((b) => ({ ...b, autoNoEnabled: v }))}
                    sx={bulkSelectSx}
                  />
                  <SniperTriState
                    label="ET"
                    tooltip="Include extra time in the auto-NO tail"
                    ariaLabel="bulk auto-no include extra time"
                    value={bulk.autoNoIncludeExtraTime}
                    onChange={(v) =>
                      setBulk((b) => ({ ...b, autoNoIncludeExtraTime: v }))
                    }
                    sx={bulkSelectSx}
                  />
                  <SniperTriState
                    label="Sell on VAR"
                    tooltip="Post the exit the moment a VAR review follows our goal"
                    ariaLabel="bulk sell on var"
                    value={bulk.sellOnVar}
                    onChange={(v) => setBulk((b) => ({ ...b, sellOnVar: v }))}
                    sx={bulkSelectSx}
                  />
                  <SniperTriState
                    label="Sell on annul"
                    tooltip="Dump to the floor once the goal is annulled"
                    ariaLabel="bulk sell on annulled goal"
                    value={bulk.sellOnAnnulledGoal}
                    onChange={(v) =>
                      setBulk((b) => ({ ...b, sellOnAnnulledGoal: v }))
                    }
                    sx={bulkSelectSx}
                  />
                  <SniperTriState
                    label="Rebuy"
                    tooltip="All-clear rebuy if the VAR confirms the goal"
                    ariaLabel="bulk var rebuy enabled"
                    value={bulk.varRebuyEnabled}
                    onChange={(v) => setBulk((b) => ({ ...b, varRebuyEnabled: v }))}
                    sx={bulkSelectSx}
                  />
                  <SniperConfigPopover
                    value={{
                      ladderBands: bulk.ladder?.ladderBands ?? defaultLadderBands(),
                      floorPrice: bulk.ladder?.floorPrice ?? null,
                      varPreventiveExitOffset:
                        bulk.ladder?.varPreventiveExitOffset ??
                        DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
                    }}
                    // NEVER required here. Each row carries its own floor — it
                    // sits below that market's pre-event price, so they
                    // legitimately differ — and `draftIncompleteReason` already
                    // demands one per draft before it can be saved. Demanding it
                    // in the BULK editor would mean the only way to promote 12
                    // rows to 3 rungs is stamping one floor over all 12.
                    floorRequired={false}
                    // Blank here means "leave each row's own floor alone", which
                    // is expressible — these are drafts, not a patch.
                    floorClearable
                    onApply={(next, touched) =>
                      setBulk((b) => {
                        // OR-MERGE: a reopened popover diffs against the seed it
                        // opened on, so replacing the flags wholesale would drop
                        // the previous session's edit before Apply to all.
                        const merged = {
                          ladderBands:
                            !!b.ladder?.touched.ladderBands || touched.ladderBands,
                          floorPrice:
                            !!b.ladder?.touched.floorPrice || touched.floorPrice,
                          varPreventiveExitOffset:
                            !!b.ladder?.touched.varPreventiveExitOffset ||
                            touched.varPreventiveExitOffset,
                        };
                        // Nothing edited = nothing staged, so the button does not
                        // advertise a ladder that would write nothing.
                        return {
                          ...b,
                          ladder:
                            merged.ladderBands ||
                            merged.floorPrice ||
                            merged.varPreventiveExitOffset
                              ? {
                                  ladderBands: next.ladderBands,
                                  floorPrice: next.floorPrice,
                                  varPreventiveExitOffset:
                                    next.varPreventiveExitOffset ??
                                    DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
                                  touched: merged,
                                }
                              : null,
                        };
                      })
                    }
                    renderTrigger={(openPopover) => (
                      <Tooltip title="Ladder config — stamped onto every draft by Apply to all.">
                        <Button
                          size="small"
                          variant={bulk.ladder ? 'contained' : 'outlined'}
                          onClick={openPopover}
                          // The label becomes the staged summary, so it needs a
                          // stable handle of its own to stay findable.
                          aria-label="bulk ladder config"
                        >
                          {bulk.ladder
                            ? ladderSummary(
                                bulk.ladder.ladderBands,
                                bulk.ladder.floorPrice,
                              )
                            : 'Ladder —'}
                        </Button>
                      </Tooltip>
                    )}
                  />
                  {/* The only way to un-stage a ladder without clearing the rest
                      of the bulk defaults (mirrors the per-event bar). */}
                  {bulk.ladder && (
                    <Tooltip title="Leave every row's ladder untouched">
                      <IconButton
                        size="small"
                        onClick={() => setBulk((b) => ({ ...b, ladder: null }))}
                        aria-label="clear bulk ladder"
                        sx={{ p: 0.25 }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Button size="small" variant="outlined" onClick={applyToAll}>
                    Apply to all
                  </Button>
                </Stack>
              )}
              {drafts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No candidates.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {drafts.map((d, i) => (
                    <CandidateRow
                      key={`${d.venue}:${d.marketId}`}
                      draft={d}
                      homeTeam={homeTeam}
                      awayTeam={awayTeam}
                      onChange={(next) =>
                        setDrafts((prev) =>
                          prev
                            ? prev.map((p, j) => (j === i ? next : p))
                            : prev,
                        )
                      }
                    />
                  ))}
                </Stack>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="caption" color="text.secondary">
                  {completeDrafts.length} of {drafts.length} ready to save
                </Typography>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={confirm}
          disabled={completeDrafts.length === 0 || createMut.isLoading}
        >
          {createMut.isLoading
            ? 'Saving…'
            : `Add ${completeDrafts.length} market(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
